/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN: any cache, debounce, or timeout used in multiple places—or encoding
 * a product decision—lives here,
 * not as a component-local `const`.
 *
 * These values were duplicated
 * in ~12 files as `5 * 60 * 1000` and `300_000`.
 * Policy changes required every edit
 * and risked divergence. Import here.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Milliseconds in a minute. Base for deriving all other durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in an hour. Used by foreground reminders window.*/
export const ONE_HOUR_MS = MINUTES_PER_HOUR * MINUTE_MS;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally through PowerSync, so a generous `staleTime` (5 min)
 * (5 min) avoids redundant refetches/re-renders without any real risk of
 * data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * Kept as its own constant (although it currently matches the offline cache
 * value) because it is an independent decision: it must stay aligned across
 * `useSession`, the `_app` route guard, and `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Timeout cap for `/api/auth/get-session` `await` in route guards.
 *
 * P23/CC-60 · I1-bis. This number previously lived as a LOCAL literal inside the
 * `beforeLoad` of `_app/route.tsx`, which is why `_auth/route.tsx` —its sibling—
 * was left without the same defense: with the response HELD (hung: neither 401 nor
 * 5xx, which resolve quickly), the `_auth` guard never resolved, the router
 * never mounted the route, and `/login` remained with EMPTY `document.body.innerText`
 * indefinitely (measured at 3/6/10/15/20 s, also on the production
 * bundle). SINGLE source of truth for BOTH guards: having it in two literals is
 * exactly what left one without the defense the other had.
 *
 * Past the timeout the screen RENDERS; the request remains in flight and synchronizes
 * machine + cache when it arrives, and `BootManager` redirects afterward if a
 * session is found. You pay a flicker, never a blank screen.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs that persist while typing (title and notes in
 * detail, subtasks). Balances typing responsiveness with mutation volume.
 */
export const DEBOUNCE_DELAY_MS = 400;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must comfortably exceed the time the browser takes to consume the
 * `<a download>` href; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
